import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { message, Modal, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { EChartsInstance } from 'echarts-for-react';
import ReactECharts from 'echarts-for-react';
import html2canvas from 'html2canvas';

import { MODAL_FOOTER_BUTTON_SIZE } from '@/shared/lib/constants';
import { exportContentToPdf } from '@/shared/lib/utils/exportPdf';
import { formatMetricValue } from '@/shared/lib/utils/number';
import { Button } from '@/shared/ui/Button';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';

import schemasTabStyles from '@/widgets/asset-detail/SchemasTab/SchemasTab.module.css';

import styles from './ExecutionResultModal.module.css';

type MetricData = {
  name: string;
  currentValue: number;
  history: { step: number; value: number }[];
};

type ExecutionResultModalProps = {
  open: boolean;
  onClose: () => void;
  metrics: MetricData[];
  runName: string;
};

export const ExecutionResultModal: React.FC<ExecutionResultModalProps> = ({ open, onClose, metrics, runName }) => {
  const chartRefs = useRef<Array<EChartsInstance | null>>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const chartsGridRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const metricsWithHistory = useMemo(() => metrics.filter((m) => m.history && m.history.length > 0), [metrics]);

  const lineColor = useMemo(
    () =>
      getComputedStyle(document.documentElement)
        .getPropertyValue('--opds-sem-color-container-standard-level4')
        .trim() || '#1890ff',
    []
  );

  useEffect(() => {
    if (!open) return;

    const resizeCharts = () => {
      chartRefs.current.forEach((chartRef) => {
        if (chartRef) {
          chartRef.resize();
        }
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      resizeCharts();
    });

    if (chartsGridRef.current) {
      resizeObserver.observe(chartsGridRef.current);
    }

    const timer = setTimeout(() => {
      resizeCharts();
    }, 100);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [open, metricsWithHistory]);

  const handleExportReport = async () => {
    await exportContentToPdf({
      contentRef,
      fileName: runName,
      setIsExporting,
    });
  };

  const handleCopyMetrics = useCallback(async () => {
    if (!contentRef.current) {
      message.error('복사할 내용이 없습니다');
      return;
    }

    if (!navigator.clipboard) {
      message.error('클립보드가 지원되지 않습니다');
      return;
    }

    try {
      setIsCopying(true);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      canvas.toBlob(async (blob) => {
        if (!blob) {
          message.error('이미지 생성에 실패했습니다');
          setIsCopying(false);
          return;
        }

        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob,
            }),
          ]);
          message.success('이미지가 클립보드에 복사되었습니다');
        } catch (error) {
          console.error('Clipboard write failed:', error);
          message.error('클립보드 복사에 실패했습니다');
        } finally {
          setIsCopying(false);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Copy failed:', error);
      message.error('이미지 복사에 실패했습니다');
      setIsCopying(false);
    }
  }, []);

  const metricColumns = useMemo<ColumnsType<MetricData>>(
    () => [
      {
        title: 'Metric',
        dataIndex: 'name',
        key: 'name',
        width: '50%',
        render: (name: string) => (
          <EllipsisText text={name} tooltipPlacement="topLeft">
            {name}
          </EllipsisText>
        ),
      },
      {
        title: 'Value',
        dataIndex: 'currentValue',
        key: 'value',
        align: 'right',
        width: '50%',
        render: (value: number) => formatMetricValue(value),
      },
    ],
    []
  );

  const getChartOption = (metric: MetricData) => ({
    title: {
      text: metric.name,
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      confine: false,
      extraCssText: 'max-width: min(360px, 90vw); z-index: 11000; overflow-wrap: anywhere;',
      formatter: (params: unknown) => {
        const paramsArray = params as Array<{ axisValue: number; value: number }>;
        const point = paramsArray[0];
        return `Step ${point.axisValue}<br/>${metric.name}: ${formatMetricValue(point.value)}`;
      },
    },
    xAxis: {
      type: 'category',
      data: metric.history.map((h) => h.step),
      name: 'Step',
    },
    yAxis: {
      type: 'value',
    },
    series: [
      {
        data: metric.history.map((h) => h.value),
        type: 'line',
        smooth: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: '#000000',
        },
        lineStyle: {
          color: lineColor,
          width: 2,
        },
      },
    ],
    grid: {
      left: '12%',
      right: '5%',
      bottom: '15%',
      top: '20%',
    },
  });

  return (
    <Modal
      title="실행 결과"
      open={open}
      onCancel={onClose}
      width={1200}
      style={{ top: 40 }}
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        },
      }}
      footer={
        <div className={styles.footerButtons}>
          <Button
            variant="outlined"
            color="standard"
            prefix={<Icon name="copy" size={16} />}
            onClick={handleCopyMetrics}
            disabled={isExporting || isCopying}
            {...MODAL_FOOTER_BUTTON_SIZE}>
            복사하기
          </Button>
          <Button
            variant="outlined"
            color="standard"
            prefix={<Icon name="download" size={16} />}
            onClick={handleExportReport}
            disabled={isExporting || isCopying}
            {...MODAL_FOOTER_BUTTON_SIZE}>
            내보내기
          </Button>
        </div>
      }>
      <div ref={contentRef} className={styles.container}>
        {metricsWithHistory.length > 0 && (
          <>
            <div className={styles.chartsSection}>
              <div
                ref={chartsGridRef}
                className={styles.chartsGrid}
                style={{
                  gridTemplateColumns:
                    metricsWithHistory.length === 1
                      ? '1fr'
                      : metricsWithHistory.length === 2
                        ? 'repeat(2, 1fr)'
                        : 'repeat(3, 1fr)',
                }}>
                {metricsWithHistory.map((metric, index) => (
                  <div key={metric.name} className={styles.chartItem}>
                    <ReactECharts
                      ref={(ref) => {
                        if (ref) {
                          chartRefs.current[index] = ref.getEchartsInstance();
                        }
                      }}
                      option={getChartOption(metric)}
                      style={{ height: '250px', width: '100%' }}
                      opts={{ renderer: 'svg' }}
                      notMerge={true}
                      lazyUpdate={false}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.divider} />
          </>
        )}

        <div className={styles.metricsSummary}>
          <Table<MetricData>
            className={schemasTabStyles.schemasTable}
            tableLayout="fixed"
            columns={metricColumns}
            dataSource={metrics}
            rowKey={(record, index) => `${record.name}-${index}`}
            pagination={false}
          />
        </div>
      </div>

      {(isExporting || isCopying) && (
        <div className={styles.exportOverlay}>
          <div className={styles.exportContent}>
            <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            <div className={styles.exportText}>{isExporting ? '내보내는 중...' : '이미지 복사 중...'}</div>
          </div>
        </div>
      )}
    </Modal>
  );
};
